import { AppUrls } from "../app";
import { Logger } from "../utils/logger";
import { MarkdownUtils } from "../utils/markdown";

/*
 * SLOT BEHAVIOR EXPLANATION
 * -------------------------
 * This component uses a <slot> to allow projecting text or HTML inside the button.
 * Example usage:
 *     <create-tab-comment-form>Leave a comment</create-tab-comment-form>
 * Without special handling, setting `this.innerHTML = ''` would erase the projected
 * children before the slot has a chance to render them. To preserve and render them:
 *
 * 1. `Array.from(this.childNodes)` captures light DOM nodes before clearing.
 * 2. `this.innerHTML = ''` clears old internal structure safely.
 * 3. A <slot> element is inserted in the internal button template.
 * 4. `slot.replaceWith(...slotted)` manually projects the preserved children
 *    where the <slot> was located.
 *
 * This ensures that the user's inline content is rendered inside the custom element's
 * internal structure, without requiring Shadow DOM or losing flexibility.
 */

enum _Mode {
    StartButton,
    Editor,
    Preview,
}

class CreateCommentForm extends HTMLElement {
    markdownQuickstartLink =
        'https://docs.github.com/en/get-started/writing-on-github/getting-started-with-writing-and-formatting-on-github/basic-writing-and-formatting-syntax';

    private _mode: _Mode = _Mode.StartButton;
    private _saving: boolean = false;
    private _errors: string[] = [];

    private _startButtonView?: HTMLElement;
    private _editorView?: HTMLElement;
    private _previewView?: HTMLElement;

    private _errorElement?: HTMLElement;
    private _textInput?: HTMLTextAreaElement;
    private _previewOutput?: HTMLElement;
    private _cancelButton?: HTMLButtonElement;
    private _startButton?: HTMLButtonElement;
    private _showPreviewButton?: HTMLButtonElement;
    private _backToEditorButton?: HTMLButtonElement;
    private _saveButton?: HTMLButtonElement;

    protected loggerTag(): string {
        throw new Error("loggerTag() must be implemented in subclass");
    }

    protected createUrl(): string {
        throw new Error("createUrl() must be implemented in subclass");
    }

    protected createData(markdown: string): object {
        throw new Error("createData() must be implemented in subclass");
    }

    connectedCallback(): void {
        Logger.info(this.loggerTag(), 'Component connected');
        const slotted = Array.from(this.childNodes);
        this.innerHTML = '';

        this._buildStartButtonView();
        this._buildEditorView();
        this._buildPreviewView();

        // Retain the html given from the tag in the page
        const slot = this._startButtonView.querySelector('slot');
        if (slot) slot.replaceWith(...slotted);

        this._addTextInputListener();
        this._addButtonClickListeners();
        this._setState();
    }

    private _buildStartButtonView() {
        this._startButtonView = document.createElement('div');
        this._startButtonView.classList.add('start-button-view');
        this._startButtonView.style.display = 'flex';
        this._startButtonView.innerHTML = `
            <button class="text-button primary start-button">
                <slot></slot>
            </button>
        `;
        this._startButton = this._startButtonView.querySelector('.start-button');
        this.append(this._startButtonView);
    }

    private _buildEditorView() {
        this._editorView = document.createElement('div');
        this._editorView.classList.add('editor-view');
        this._editorView.style.display = 'none';
        this._editorView.innerHTML = `
            <a href="${this.markdownQuickstartLink}" target="_blank">
                <button type="button" class="text-button primary font-size-small italics">
                    Learn how to use markdown
                </button>
            </a>
            <textarea class="comment-text-input card-style"></textarea>
            <div class="control-buttons">
                <button class="text-button primary cancel-button">Cancel</button>
                <button class="raised-button primary show-preview-button">Preview</button>
            </div>
        `;
        this._textInput = this._editorView.querySelector('.comment-text-input')!;
        this._cancelButton = this._editorView.querySelector('.cancel-button')!;
        this._showPreviewButton = this._editorView.querySelector('.show-preview-button')
        this.append(this._editorView);
    }

    private _buildPreviewView() {
        this._previewView = document.createElement('div');
        this._previewView.classList.add('preview-view');
        this._previewView.style.display = 'none';
        this._previewView.innerHTML = `
            <div class="comment-preview markdown"></div>
            <p class="errors font-size-small"></p>
            <div class="control-buttons">
                <button class="raised-button primary back-to-editor-button">Edit</button>
                <button class="raised-button primary save-button">Save</button>
            </div>
        `;
        this._previewOutput = this._previewView.querySelector('.comment-preview')!;
        this._errorElement = this._previewView.querySelector('.errors')!;
        this._backToEditorButton = this._previewView.querySelector('.back-to-editor-button');
        this._saveButton = this._previewView.querySelector('.save-button');
        this.append(this._previewView);
    }

    private _addTextInputListener() {
        if (!this._textInput || !this._showPreviewButton) return;
        this._textInput.addEventListener('input', this._setState.bind(this));
    }

    private _addButtonClickListeners() {
        if (this._startButton) {
            this._startButton.addEventListener('click', () => {
                Logger.info(this.loggerTag(), 'Start button clicked → entering editor mode');
                this._mode = _Mode.Editor;
                this._setState();
            });
        }

        if (this._cancelButton) {
            this._cancelButton.addEventListener('click', () => {
                Logger.info(this.loggerTag(), 'Cancel clicked → returning to start');
                this._mode = _Mode.StartButton;
                this._errors = [];
                this._setState();
            });
        }

        if (this._showPreviewButton) {
            this._showPreviewButton.addEventListener('click', () => {
                Logger.info(this.loggerTag(), 'Preview clicked → entering preview mode');
                const markdown = this._textInput?.value.trim() || '';
                const rendered = MarkdownUtils.render(markdown);
                if (this._previewOutput) this._previewOutput.innerHTML = rendered;
                this._mode = _Mode.Preview;
                this._setState();
            });
        }

        if (this._backToEditorButton) {
            this._backToEditorButton.addEventListener('click', () => {
                Logger.info(this.loggerTag(), 'Back to editor from preview');
                this._mode = _Mode.Editor;
                this._setState();
            });
        }

        if (this._saveButton) {
            this._saveButton.addEventListener('click', this._save.bind(this));
        }
    }

    private _setState() {
        if (this._startButtonView) {
            this._startButtonView.style.display = this._mode === _Mode.StartButton ? 'flex' : 'none';
        }
        if (this._editorView) {
            this._editorView.style.display = this._mode === _Mode.Editor ? 'flex' : 'none';
        }
        if (this._previewView) {
            this._previewView.style.display = this._mode === _Mode.Preview ? 'flex' : 'none';
        }
        if (this._cancelButton) {
            this._cancelButton.style.display = this._mode === _Mode.StartButton ? 'none' : 'inline-block';
        }
        if (this._errorElement) {
            this._errorElement.innerHTML = this._errors.join('<br>');
            this._errorElement.style.display = this._errors.length ? 'block' : 'none';
        }
        if (this._textInput) {
            const hasText = this._textInput!.value.trim().length > 0;
            this._showPreviewButton.disabled = !hasText;
        }
        if (this._backToEditorButton) {
            this._backToEditorButton.disabled = this._saving;
        }
        if (this._saveButton) {
            this._saveButton.disabled = this._saving;
        }
    }

    private async _save() {
        Logger.info(this.loggerTag(), 'Save initiated');
        this._saving = true;
        this._errors = [];
        this._setState();
        try {
            const markdown = this._textInput.value.trim();
            const payload = this.createData(markdown);
            const url = this.createUrl();
            const fetchPromise = fetch(url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const minWait = new Promise(resolve => setTimeout(resolve, 500));
            const [response] = await Promise.all([fetchPromise, minWait]);
            if (response.ok) {
                Logger.info(this.loggerTag(), 'Save successful — reloading');
                location.reload();
            } else {
                const data = await response.json();
                this._errors = data.errors || [response.statusText];
                Logger.warn(this.loggerTag(), `Save failed: ${this._errors.join(', ')}`);
            }
        } catch (e) {
            this._errors = ['An error occurred while saving.'];
            Logger.error(this.loggerTag(), `Save error - ${e}`);
        } finally {
            this._saving = false;
            this._setState();
        }
    }
}

export class CreateTabCommentForm extends CreateCommentForm {
    static htmlTag: 'create-tab-comment-form' = 'create-tab-comment-form';

    protected loggerTag(): string {
        return 'CreateTabCommentForm';
    }

    protected createUrl(): string {
        return AppUrls.createCommentEndpoint;
    }

    protected createData(markdown: string): object {
        const tabIdStr = this.getAttribute('tab-id');
        const tabId = parseInt(tabIdStr);
        const parentIdStr = this.getAttribute('parent-comment-id');
        let parentId = null;
        if (parentIdStr !== null && parentIdStr !== 'null') {
            parentId = parseInt(parentIdStr);
        }
        return {
            tab_id: tabId,
            parent_id: parentId,
            text: markdown,
        };
    }
}

export class CreateForumCommentForm extends CreateCommentForm {
    static htmlTag: 'create-forum-comment-form' = 'create-forum-comment-form';

    protected loggerTag(): string {
        return 'CreateForumCommentForm';
    }

    protected createUrl(): string {
        return '/api/comments/create-forum-comment';
    }

    protected createData(markdown: string): object {
        const forumPostIdStr = this.getAttribute('forum-post-id');
        const forumPostId = parseInt(forumPostIdStr);
        return {
            forum_post_id: forumPostId,
            body: markdown
        };
    }
}